<template>
  <div class="plot-configurator">
    <div class="pc-header">
      <i :class="figureType.icon"></i>
      <div>
        <div class="pc-title">{{ figureType.name }}</div>
        <div class="pc-desc text-muted small">{{ figureType.description }}</div>
      </div>
    </div>

    <div v-if="!fieldsAvailable.length" class="alert alert-warning small">
      No plottable fields were found in this dataset. Try selecting a
      different dataset or a different figure type.
    </div>

    <form v-else @submit.prevent="handleGenerate" class="pc-form">
      <!-- Common: title + axis labels -->
      <details class="pc-section" open>
        <summary>Axes &amp; Labels</summary>
        <div class="pc-body">
          <div class="form-group">
            <label>Figure title</label>
            <input
              v-model="localConfig.title"
              class="form-control form-control-sm"
              @change="updateConfig"
              placeholder="Optional title shown above the chart"
            />
          </div>

          <div v-if="needsXField" class="form-group">
            <label>{{ labelForX }}</label>
            <select
              v-model="localConfig.xAxis"
              class="form-control form-control-sm"
              @change="updateConfig"
            >
              <option value="">— pick a field —</option>
              <option v-for="c in xOptions" :key="c" :value="c">{{ c }}</option>
            </select>
          </div>

          <div v-if="needsYField" class="form-group">
            <label>{{ labelForY }}</label>
            <select
              v-model="localConfig.yAxis"
              class="form-control form-control-sm"
              @change="updateConfig"
            >
              <option value="">— pick a numeric field —</option>
              <option v-for="c in numericFields" :key="c" :value="c">{{ c }}</option>
            </select>
          </div>

          <!-- Grouped bar: multiple Y fields -->
          <div v-if="figureType.id === 'grouped_bar'" class="form-group">
            <label>Additional Y fields (compared side-by-side)</label>
            <div class="pc-field-toggles">
              <label
                v-for="f in numericFields"
                :key="f"
                class="pc-chip"
                :class="{ active: (localConfig.yFields || []).includes(f) }"
              >
                <input
                  type="checkbox"
                  :value="f"
                  v-model="localConfig.yFields"
                  @change="updateConfig"
                />
                {{ f }}
              </label>
            </div>
          </div>

          <!-- Correlation: pick which columns -->
          <div v-if="figureType.id === 'correlation'" class="form-group">
            <label>Columns to correlate</label>
            <div class="pc-field-toggles">
              <label
                v-for="f in numericFields"
                :key="f"
                class="pc-chip"
                :class="{ active: (localConfig.columns || []).includes(f) }"
              >
                <input
                  type="checkbox"
                  :value="f"
                  v-model="localConfig.columns"
                  @change="updateConfig"
                />
                {{ f }}
              </label>
            </div>
            <small class="form-text text-muted">
              Pearson correlation is computed for every pair.
            </small>
          </div>

          <div class="row">
            <div class="col-6 form-group">
              <label>X-axis label</label>
              <input
                v-model="localConfig.xLabel"
                class="form-control form-control-sm"
                @change="updateConfig"
              />
            </div>
            <div class="col-6 form-group">
              <label>Y-axis label</label>
              <input
                v-model="localConfig.yLabel"
                class="form-control form-control-sm"
                @change="updateConfig"
              />
            </div>
          </div>
        </div>
      </details>

      <!-- Aggregation (bar / grouped_bar) -->
      <details
        v-if="['bar', 'grouped_bar'].includes(figureType.id)"
        class="pc-section"
        open
      >
        <summary>Aggregation</summary>
        <div class="pc-body">
          <div class="form-group">
            <label>Reducer</label>
            <select
              v-model="localConfig.aggregation"
              class="form-control form-control-sm"
              @change="updateConfig"
            >
              <option value="mean">Mean</option>
              <option value="median">Median</option>
              <option value="sum">Sum</option>
              <option value="min">Min</option>
              <option value="max">Max</option>
              <option value="count">Count</option>
            </select>
            <small class="form-text text-muted">
              How numeric values are combined per category.
            </small>
          </div>
        </div>
      </details>

      <!-- Histogram-specific -->
      <details
        v-if="figureType.id === 'histogram'"
        class="pc-section"
        open
      >
        <summary>Binning</summary>
        <div class="pc-body">
          <div class="form-group">
            <label>Bin count: {{ localConfig.bins || 'auto' }}</label>
            <input
              type="range"
              min="5"
              max="60"
              step="1"
              v-model.number="localConfig.bins"
              @change="updateConfig"
              class="pc-slider"
            />
          </div>
          <div class="form-check">
            <input
              v-model="localConfig.showDensity"
              type="checkbox"
              id="showDensity"
              class="form-check-input"
              @change="updateConfig"
            />
            <label for="showDensity" class="form-check-label small">
              Overlay kernel density estimate
            </label>
          </div>
        </div>
      </details>

      <!-- Scatter-specific -->
      <details
        v-if="figureType.id === 'scatter'"
        class="pc-section"
      >
        <summary>Statistical overlays</summary>
        <div class="pc-body">
          <div class="form-check">
            <input
              v-model="localConfig.showRegression"
              type="checkbox"
              id="showRegression"
              class="form-check-input"
              @change="updateConfig"
            />
            <label for="showRegression" class="form-check-label small">
              Show linear regression line (with R²)
            </label>
          </div>
        </div>
      </details>

      <!-- Line/time series options -->
      <details
        v-if="['line', 'timeseries'].includes(figureType.id)"
        class="pc-section"
      >
        <summary>Line options</summary>
        <div class="pc-body">
          <div class="form-check">
            <input
              v-model="localConfig.showPoints"
              type="checkbox"
              id="showPoints"
              class="form-check-input"
              @change="updateConfig"
            />
            <label for="showPoints" class="form-check-label small">Show data points</label>
          </div>
          <div class="form-check">
            <input
              v-model="localConfig.fillArea"
              type="checkbox"
              id="fillArea"
              class="form-check-input"
              @change="updateConfig"
            />
            <label for="fillArea" class="form-check-label small">Fill area under line</label>
          </div>
          <div class="form-group mt-2">
            <label>Smoothing: {{ (localConfig.tension || 0).toFixed(2) }}</label>
            <input
              type="range"
              min="0"
              max="0.8"
              step="0.05"
              v-model.number="localConfig.tension"
              @change="updateConfig"
              class="pc-slider"
            />
          </div>
        </div>
      </details>

      <!-- Pie/donut -->
      <details
        v-if="figureType.id === 'pie'"
        class="pc-section"
        open
      >
        <summary>Pie options</summary>
        <div class="pc-body">
          <div class="form-check">
            <input
              v-model="localConfig.donut"
              type="checkbox"
              id="donut"
              class="form-check-input"
              @change="updateConfig"
            />
            <label for="donut" class="form-check-label small">
              Render as donut chart
            </label>
          </div>
        </div>
      </details>

      <!-- Cosmetics -->
      <details class="pc-section">
        <summary>Style</summary>
        <div class="pc-body">
          <div class="row">
            <div class="col-6 form-group">
              <label>Primary color</label>
              <input
                type="color"
                class="form-control form-control-sm pc-color"
                :value="primaryColorValue"
                @change="setPrimaryColor($event.target.value)"
              />
            </div>
            <div class="col-6 form-group" v-if="showPointSize">
              <label>Point size: {{ localConfig.pointSize || 4 }}</label>
              <input
                type="range"
                min="1"
                max="14"
                step="1"
                v-model.number="localConfig.pointSize"
                @change="updateConfig"
                class="pc-slider"
              />
            </div>
          </div>
          <div class="form-check">
            <input
              v-model="localConfig.showGrid"
              type="checkbox"
              id="showGrid"
              class="form-check-input"
              @change="updateConfig"
            />
            <label for="showGrid" class="form-check-label small">Show gridlines</label>
          </div>
          <div class="form-check">
            <input
              v-model="localConfig.showLegend"
              type="checkbox"
              id="showLegend"
              class="form-check-input"
              @change="updateConfig"
            />
            <label for="showLegend" class="form-check-label small">Show legend</label>
          </div>
        </div>
      </details>

      <div class="pc-actions">
        <button
          type="submit"
          class="btn btn-primary btn-sm btn-block"
          :disabled="!canGenerate"
        >
          <i class="fas fa-chart-bar"></i> Generate chart
        </button>
      </div>
    </form>
  </div>
</template>

<script>
/* eslint-disable no-console */
import ChartService, { numericColumns, allColumns, columnType } from '../services/ChartService.js'

export default {
  name: 'PlotConfigurator',
  props: {
    dataset: { type: Object, required: true },
    secondDataset: { type: Object, default: null },
    figureType: { type: Object, required: true },
    config: { type: Object, default: () => ({}) }
  },
  data () {
    return {
      localConfig: { ...this.config }
    }
  },
  computed: {
    numericFields () { return numericColumns(this.dataset) },
    allFields () { return allColumns(this.dataset) },
    fieldsAvailable () {
      return this.figureType.id === 'pie'
        ? this.allFields
        : this.numericFields
    },
    needsXField () {
      // Charts that don't need an X selector
      return !['correlation'].includes(this.figureType.id)
    },
    needsYField () {
      return !['histogram', 'density', 'correlation', 'pie'].includes(this.figureType.id)
    },
    labelForX () {
      switch (this.figureType.id) {
        case 'bar':
        case 'grouped_bar':
        case 'box': return 'Category (X)'
        case 'pie': return 'Category field'
        case 'histogram':
        case 'density': return 'Value field'
        case 'timeseries': return 'Date/time field (X)'
        default: return 'X field'
      }
    },
    labelForY () {
      return this.figureType.id === 'box' ? 'Value (Y, numeric)' : 'Y field (numeric)'
    },
    xOptions () {
      // For histogram/density/pie — any column works for categorical,
      // numeric-only for continuous.
      if (['histogram', 'density'].includes(this.figureType.id)) return this.numericFields
      if (this.figureType.id === 'pie') return this.allFields
      if (['bar', 'grouped_bar'].includes(this.figureType.id)) return this.allFields
      if (this.figureType.id === 'timeseries') {
        // Prefer columns that look date-like, fall back to all
        const dateLike = this.allFields.filter(f => columnType(this.dataset, f) === 'date')
        return dateLike.length ? dateLike : this.allFields
      }
      return this.numericFields
    },
    showPointSize () {
      return ['scatter', 'line', 'timeseries'].includes(this.figureType.id)
    },
    primaryColorValue () {
      return this.localConfig.pointColor ||
        this.localConfig.lineColor ||
        this.localConfig.barColor ||
        '#0B5ED7'
    },
    canGenerate () {
      const id = this.figureType.id
      if (id === 'correlation') {
        return (this.localConfig.columns || []).length >= 2 ||
          this.numericFields.length >= 2
      }
      if (this.needsYField) return !!this.localConfig.xAxis && !!this.localConfig.yAxis
      return !!this.localConfig.xAxis
    }
  },
  watch: {
    figureType: {
      handler () {
        this.localConfig = { ...this.config }
      },
      deep: true
    },
    config: {
      handler (v) {
        this.localConfig = { ...v }
      },
      deep: true
    },
    dataset () {
      // Reset axis fields if they no longer exist on the new dataset
      if (this.localConfig.xAxis && !this.allFields.includes(this.localConfig.xAxis)) {
        this.localConfig.xAxis = ''
      }
      if (this.localConfig.yAxis && !this.numericFields.includes(this.localConfig.yAxis)) {
        this.localConfig.yAxis = ''
      }
      this.updateConfig()
    }
  },
  methods: {
    updateConfig () {
      this.$emit('updateConfig', { ...this.localConfig })
    },
    setPrimaryColor (hex) {
      // Push the same color into whichever field the active chart uses
      this.localConfig.pointColor = hex
      this.localConfig.lineColor = hex
      this.localConfig.barColor = hex
      this.updateConfig()
    },
    handleGenerate () {
      if (!this.canGenerate) return
      this.$emit('generateChart')
    }
  },
  created () {
    // Unused import guard — allColumns is exported from ChartService for
    // completeness and also used in computed.
    // eslint-disable-next-line no-unused-expressions
    ChartService
  }
}
</script>

<style scoped>
.plot-configurator {
  font-size: 0.88rem;
}
.pc-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: #f1f8ff;
  border: 1px solid #cfe2ff;
  border-radius: 6px;
  margin-bottom: 12px;
}
.pc-header i { font-size: 1.1rem; color: #0b5ed7; }
.pc-title { font-weight: 600; color: #212529; }
.pc-desc { margin-top: 1px; }
.pc-form .pc-section {
  border: 1px solid #e5e9f0;
  border-radius: 6px;
  background: #fff;
  margin-bottom: 8px;
}
.pc-form .pc-section > summary {
  padding: 8px 12px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #495057;
  cursor: pointer;
  user-select: none;
}
.pc-form .pc-section > summary::marker,
.pc-form .pc-section > summary::-webkit-details-marker {
  color: #adb5bd;
}
.pc-form .pc-section[open] > summary {
  border-bottom: 1px solid #e5e9f0;
  background: #fafbfc;
}
.pc-form .pc-body {
  padding: 10px 12px;
}
.pc-form label {
  font-size: 0.76rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 3px;
}
.pc-field-toggles {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-height: 150px;
  overflow-y: auto;
  padding: 4px;
  border: 1px solid #e5e9f0;
  border-radius: 4px;
  background: #fafbfc;
}
.pc-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.78rem;
  padding: 2px 8px;
  border: 1px solid #e5e9f0;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  margin: 0;
  font-weight: 500;
  color: #495057;
}
.pc-chip input { display: none; }
.pc-chip.active {
  background: #e9f3fe;
  border-color: #0b5ed7;
  color: #0b5ed7;
}
.pc-chip:hover { border-color: #0b5ed7; }
.pc-slider { width: 100%; accent-color: #0b5ed7; }
.pc-color { height: 34px; padding: 2px; }
.pc-actions { margin-top: 12px; }
.btn-block { display: block; width: 100%; }
</style>
